// src/components/AnimatedOnScroll.tsx
import React, { useRef, useEffect, useState } from 'react';

interface AnimatedOnScrollProps {
  children: React.ReactNode;
  className?: string;
  animationClass: string; // e.g., 'fade-in-up', 'slide-in-left'
  threshold?: number; // 0.0 to 1.0 (how much of the element must be visible to trigger)
  rootMargin?: string; // e.g., '0px 0px -100px 0px'
}

const AnimatedOnScroll: React.FC<AnimatedOnScrollProps> = ({
  children,
  className,
  animationClass,
  threshold = 0.2, // Default: 20% of the element visible
  rootMargin = '0px',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger setIsVisible(true) once when it becomes visible
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
        // If you want to reset the animation when it scrolls out of view:
        // if (!entry.isIntersecting && isVisible) {
        //   setIsVisible(false);
        // }
      },
      {
        root: null, // The viewport
        rootMargin,
        threshold,
      }
    );

    const currentElement = elementRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    // Cleanup function: disconnect the observer when the component unmounts
    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible, threshold, rootMargin]); // Re-run effect if these props change

  const animationClasses = isVisible
    ? `animate-${animationClass} opacity-0` // Start hidden, then animate
    : 'opacity-0'; // Keep hidden if not visible yet

  return (
    <div
      ref={elementRef}
      className={`${className} transition-opacity ${animationClasses}`}
    >
      {children}
    </div>
  );
};

export default AnimatedOnScroll;